import React from "react";
import { StyleSheet, View, ScrollView } from "react-native";
import { ThemedText } from "@/components/ThemedText";
import { ThemedInput } from "@/components/ThemedInput";
import { FilterChip } from "./FilterChip";
import { useThemeColor } from "@/hooks/useThemeColor";
import { IMediaFilterCriteria, MemoryModel, Contact, ContactModel, GroupModel } from "common247";

export interface IMediaFilterComponentProps {
  criteria: IMediaFilterCriteria;
  onChange: (updated: IMediaFilterCriteria) => void;
  allMemories?: MemoryModel[];
  allContacts?: (Contact | ContactModel)[];
  allGroups?: GroupModel[];
}

const MEDIA_TYPES: ("all" | "image" | "video" | "audio" | "document" | "pdf" | "other")[] = [
  "all",
  "image",
  "video",
  "audio",
  "document",
  "pdf",
  "other",
];

const CATEGORIES = [
  { key: "all", label: "All Categories" },
  { key: "standard", label: "Standard" },
  { key: "generated", label: "AI Generated" },
  { key: "documents", label: "Documents" },
  { key: "emoji", label: "Emoji" },
  { key: "sticker", label: "Sticker" },
];

const RATINGS = [1, 2, 3, 4, 5];

/**
 * Filter controls specific to Media entities.
 */
export function MediaFilter({
  criteria,
  onChange,
  allMemories = [],
  allContacts = [],
  allGroups = [],
}: IMediaFilterComponentProps): React.JSX.Element {
  const textMuted = useThemeColor({}, "textMuted");

  const currentTypes = criteria.mediaTypes || ["all"];
  const currentCategory = criteria.category || "all";
  const currentRating = criteria.minRating || 1;
  const currentMemoryIds = criteria.memoryIds || [];
  const currentContactIds = criteria.contactIds || [];
  const currentGroupIds = criteria.groupIds || [];

  const handleMediaTypeToggle = (type: "all" | "image" | "video" | "audio" | "document" | "pdf" | "other"): void => {
    if (type === "all") {
      onChange({ ...criteria, mediaTypes: ["all"] });
      return;
    }
    let updated: ("all" | "image" | "video" | "audio" | "document" | "pdf" | "other")[];
    if (currentTypes.includes(type)) {
      updated = currentTypes.filter((t) => t !== type && t !== "all");
      if (updated.length === 0) updated = ["all"];
    } else {
      updated = [...currentTypes.filter((t) => t !== "all"), type];
    }
    onChange({ ...criteria, mediaTypes: updated });
  };

  const handleMemoryToggle = (memoryId: string): void => {
    const updated = currentMemoryIds.includes(memoryId)
      ? currentMemoryIds.filter((id) => id !== memoryId)
      : [...currentMemoryIds, memoryId];
    onChange({ ...criteria, memoryIds: updated.length > 0 ? updated : undefined });
  };

  const handleContactToggle = (contactId: string): void => {
    const updated = currentContactIds.includes(contactId)
      ? currentContactIds.filter((id) => id !== contactId)
      : [...currentContactIds, contactId];
    onChange({ ...criteria, contactIds: updated.length > 0 ? updated : undefined });
  };

  const handleGroupToggle = (groupId: string): void => {
    const updated = currentGroupIds.includes(groupId)
      ? currentGroupIds.filter((id) => id !== groupId)
      : [...currentGroupIds, groupId];
    onChange({ ...criteria, groupIds: updated.length > 0 ? updated : undefined });
  };

  return (
    <View style={styles.container}>
      {/* Date Range */}
      <View style={styles.row}>
        <View style={styles.col}>
          <ThemedText style={[styles.label, { color: textMuted }]}>From Date</ThemedText>
          <ThemedInput
            value={criteria.dateFrom ? String(criteria.dateFrom) : ""}
            onChangeText={(txt) => onChange({ ...criteria, dateFrom: txt.trim() || undefined })}
            placeholder="e.g. 2026, 01-Jan-2026"
            style={styles.input}
          />
        </View>
        <View style={styles.col}>
          <ThemedText style={[styles.label, { color: textMuted }]}>To Date</ThemedText>
          <ThemedInput
            value={criteria.dateTo ? String(criteria.dateTo) : ""}
            onChangeText={(txt) => onChange({ ...criteria, dateTo: txt.trim() || undefined })}
            placeholder="e.g. 2026, 31-Dec-2026"
            style={styles.input}
          />
        </View>
      </View>

      {/* Media Types */}
      <View style={styles.section}>
        <ThemedText style={[styles.label, { color: textMuted }]}>Media Types</ThemedText>
        <View style={styles.chipsWrap}>
          {MEDIA_TYPES.map((type) => (
            <FilterChip
              key={type}
              label={type.toUpperCase()}
              selected={currentTypes.includes(type)}
              onPress={() => handleMediaTypeToggle(type)}
              compact
            />
          ))}
        </View>
      </View>

      {/* Media Categories */}
      <View style={styles.section}>
        <ThemedText style={[styles.label, { color: textMuted }]}>Category</ThemedText>
        <View style={styles.chipsWrap}>
          {CATEGORIES.map((cat) => (
            <FilterChip
              key={cat.key}
              label={cat.label}
              selected={currentCategory === cat.key}
              onPress={() => onChange({ ...criteria, category: cat.key === "all" ? undefined : cat.key })}
              compact
            />
          ))}
        </View>
      </View>

      {/* Minimum Rating */}
      <View style={styles.section}>
        <ThemedText style={[styles.label, { color: textMuted }]}>Minimum Rating</ThemedText>
        <View style={styles.chipsWrap}>
          {RATINGS.map((star) => (
            <FilterChip
              key={star}
              label={star === 1 ? "Any Rating" : `${star}★ & up`}
              selected={currentRating === star}
              onPress={() => onChange({ ...criteria, minRating: star })}
              compact
            />
          ))}
        </View>
      </View>

      {/* Associated Memories */}
      {allMemories.length > 0 && (
        <View style={styles.section}>
          <ThemedText style={[styles.label, { color: textMuted }]}>Associated Memories</ThemedText>
          <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.horizScroll}>
            {allMemories.map((m) => (
              <FilterChip
                key={m.id}
                label={m.title}
                icon="book-outline"
                selected={currentMemoryIds.includes(m.id || "")}
                onPress={() => m.id && handleMemoryToggle(m.id)}
                compact
              />
            ))}
          </ScrollView>
        </View>
      )}

      {/* Associated Contacts */}
      {allContacts.length > 0 && (
        <View style={styles.section}>
          <ThemedText style={[styles.label, { color: textMuted }]}>Associated Contacts</ThemedText>
          <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.horizScroll}>
            {allContacts.map((c) => (
              <FilterChip
                key={c.id}
                label={c.displayName}
                icon="person-outline"
                selected={currentContactIds.includes(c.id || "")}
                onPress={() => c.id && handleContactToggle(c.id)}
                compact
              />
            ))}
          </ScrollView>
        </View>
      )}

      {/* Associated Groups */}
      {allGroups.length > 0 && (
        <View style={styles.section}>
          <ThemedText style={[styles.label, { color: textMuted }]}>Associated Groups</ThemedText>
          <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.horizScroll}>
            {allGroups.map((g) => (
              <FilterChip
                key={g.id}
                label={g.name}
                icon="people-outline"
                selected={currentGroupIds.includes(g.id || "")}
                onPress={() => g.id && handleGroupToggle(g.id)}
                compact
              />
            ))}
          </ScrollView>
        </View>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    paddingVertical: 4,
  },
  row: {
    flexDirection: "row",
    gap: 10,
    marginBottom: 8,
  },
  col: {
    flex: 1,
  },
  section: {
    marginBottom: 8,
  },
  label: {
    fontSize: 12,
    fontWeight: "600",
    marginBottom: 4,
  },
  input: {
    paddingVertical: 6,
    paddingHorizontal: 8,
    fontSize: 13,
  },
  chipsWrap: {
    flexDirection: "row",
    flexWrap: "wrap",
  },
  horizScroll: {
    flexDirection: "row",
  },
});
